import { useEffect, useRef, useState } from "react";
import * as uuid from "uuid";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { VigilanceIcon } from "@/Common/Components/IconControls/VigilanceIcon";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { Tooltip } from "@/Common/Components/Tooltip/Tooltip";
import { useLazyRef } from "@/Common/CustomHooks/useLazyRef";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { PrescriptionHelper } from "@/Common/Helpers/PrescriptionHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LongTermMedicationIcon } from "@/Patient/Components/Medication/LongTermMedicationIcon";
import { MedicationHelper } from "@/Patient/Helpers/MedicationHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { VigilanceQueryViewerPopupHelper } from "@/Patient/Helpers/VigilanceQueryViewerPopupHelper";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationsOrigin, MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { fetchMedicationHistoryPDF } from "../Api/PrescriptionRenewalsClient";
import { PrescriptionRenewalHistoryOrderType } from "../Common/PrescriptionRenewalHistoryOrderType";
import { PrescriptionRenewalMedicationHistoryFilterSettingModel } from "../Models/PrescriptionRenewalMedicationHistoryFilterSettingModel";
import { PrescriptionRenewalsMedicationHistoryItem } from "../Models/PrescriptionRenewalsMedicationHistoryItem";
import { PrescriptionRenewalsMedicationHistoryFilterSettings } from "./PrescriptionRenewalsMedicationHistoryFilterSettings";

import styles from "./PrescriptionRenewalsViewMedicationHistory.module.scss";

interface PrescriptionRenewalsViewMedicationHistoryProps {
    prescriptionRenewalId: string;
    patientSummary: IPatientMedicalSummaryModel;
    patientProfile: Patient;
    refreshPatientData: () => void;
}

const defaultStatus = ["ActiveLabel", "Expired3Label"];
const saveOptionKey: string = "RenewalsHistoryInbox";
const fixedFormat = "yyyy-MM-dd";
const medicationColumnKey = "medication";
const beginningColumnKey = "beginning";
const endColumnKey = "end";
const prescriberColumnKey = "prescriber";
const quantityColumnKey = "quantity";
const dosageColumnKey = "dosage";
const writtenDateColumnKey = "writtenDate";
const oldestDate = new Date(null);
const redRowString: string = "red-row";
const greenRowString: string = "green-row";

const loadTableRenderOptions = (columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions => {
    const defaultTablePluginOptions = {
        MinColWidth: 50,
        MinTableWidth: 950,
        Cols: columnOptions.filter(x => !x.IsHidden).map(i => i.WidthPercentage),
        FirstColumnNoDrag: false,
        LastColumnNoDrag: true,
        Autoresize: true,
        OnColumnWidthChange: null
    };
    return Immutable.fromJS(defaultTablePluginOptions);
};

function PrescriptionRenewalsViewMedicationHistory(props: PrescriptionRenewalsViewMedicationHistoryProps): JSX.Element {
    const { getText } = useTranslation();
    const cachedEmployees = useRef<Record<string, Employee>>({});
    const cachedMedicationHistoryItems = useRef<PrescriptionRenewalsMedicationHistoryItem[]>([]);
    const [queryOrderType, setQueryOrderType] = useState<PrescriptionRenewalHistoryOrderType>(PrescriptionRenewalHistoryOrderType.Medication);
    const [queryOrderDirection, setQueryOrderDirection] = useState<SortDirection>(SortDirection.ASC);
    const [medicationHistoryItem, setMedicationHistoryItem] = useState<Immutable.List<TableDataSourceItem<PrescriptionRenewalsMedicationHistoryItem>>>(() => Immutable.List([]));
    const [searchText, setSearchText] = useState<string>("");
    const [isPharmaNetLoginOpen, setIsPharmaNetLoginOpen] = useState<boolean>(false);
    const [isFilterSettingsOpen, setIsFilterSettingsOpen] = useState<boolean>(false);
    const [isColumnOrderingPopupVisible, setIsColumnOrderingPopupVisible] = useState<boolean>(false);
    const [filterSettings, setFilterSettings] = useState<PrescriptionRenewalMedicationHistoryFilterSettingModel>(() => new PrescriptionRenewalMedicationHistoryFilterSettingModel({
        selectedStatus: defaultStatus
    }));
    const latestFilterSettings = useLazyRef<PrescriptionRenewalMedicationHistoryFilterSettingModel>(() => new PrescriptionRenewalMedicationHistoryFilterSettingModel());

    const getRefinedStatus = (currentStatus: string): string => {
        if (currentStatus === getText("ActiveLabel")) {
            return currentStatus;
        }

        if (currentStatus === getText("CompletedLabel")) {
            return getText("Completed2Label");
        }

        if (currentStatus === getText("ExpiredLabel")) {
            return getText("Expired3Label");
        }

        if (MedicationModel.validateCeasedMedicationByVocab(currentStatus)) {
            return getText("CeasedLabel");
        }

        return "";
    };

    const setFilteredAndSortedTableData = (): void => {
        if (!cachedMedicationHistoryItems.current)
            return;

        const filteredMedicationHistoryItems = cachedMedicationHistoryItems.current.filter(p =>
            p.isExpiredLongTermMed
            || (
                MedicationHelper.isMedicationDateBetweenDateFromAndDateTo(p.beginning, p.end, filterSettings.dateFrom, filterSettings.dateTo)
                && (filterSettings.selectedStatus.length === 0 || filterSettings.selectedStatus.any(status => getText(status).includes(getRefinedStatus(p.status))))
                && (filterSettings.selectedOrigin.length === 0 || filterSettings.selectedOrigin.includes(p.origin))
                && (!searchText || PrescriptionRenewalsMedicationHistoryItem.isSearchTextIncluded(p, searchText))
            ));

        const medicationsTable = filteredMedicationHistoryItems.map(medicationHistoryItem => new TableDataSourceItem(medicationHistoryItem));

        setMedicationHistoryItem(Immutable.List(medicationsTable));
    };

    const getMyleTableColumnsDefaultOptions = (): MyleTableColumnOptions[] => {
        const getCommonTableColumnOptions = (headerText: string, fieldName: string, sortColumn: PrescriptionRenewalHistoryOrderType, WidthPercentage: number, isHidden: boolean = false): MyleTableColumnOptions => {
            function sortMedicationsBy(orderType: PrescriptionRenewalHistoryOrderType, sortDirection: SortDirection): void {
                const isDescending: boolean = sortDirection === SortDirection.DESC;

                switch (orderType) {
                    case PrescriptionRenewalHistoryOrderType.Medication:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.medication ?? "", isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Origin:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.origin ?? "", isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Beginning:
                        cachedMedicationHistoryItems.current.sortByProperty(p => !!p.beginning ? new Date(p.beginning) : oldestDate, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.End:
                        cachedMedicationHistoryItems.current.sortByProperty(p => !!p.end ? new Date(p.end) : oldestDate, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Dosage:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.dosage ?? 0, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Refills:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.refills ?? 0, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Duration:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.duration ?? 0, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Quantity:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.quantity ?? [], isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.Prescriber:
                        cachedMedicationHistoryItems.current.sortByProperty(
                            p =>
                                cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && PrescriptionHelper.IsPrescribedExternally(p.externalPrescriberDisplayName)
                                    ? p.externalPrescriberDisplayName ?? ""
                                    : Employee.getFullNameWithPrefixContactFacet(p.prescriber?.ContactFacet_id) ?? "",
                            isDescending
                        );
                        break;
                    case PrescriptionRenewalHistoryOrderType.Status:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.status, isDescending);
                        break;
                    case PrescriptionRenewalHistoryOrderType.WrittenDate:
                        cachedMedicationHistoryItems.current.sortByProperty(p => p.writtenDate ? new Date(p.writtenDate) : oldestDate, isDescending);
                        break;
                }
            }

            function tableSort(newOrderType: PrescriptionRenewalHistoryOrderType): void {
                const isOrderTypeDifferent = queryOrderType !== newOrderType;
                if (isOrderTypeDifferent) {
                    setQueryOrderType(newOrderType);
                }

                const newSortDirection = MyleTableHelper.getNewSortDir(newOrderType, queryOrderType, queryOrderDirection);
                const isOrderDirectionDifferent = queryOrderDirection !== newSortDirection;
                if (isOrderDirectionDifferent) {
                    setQueryOrderDirection(newSortDirection);
                }

                if (isOrderTypeDifferent || isOrderDirectionDifferent) {
                    sortMedicationsBy(newOrderType, newSortDirection);
                }

                setFilteredAndSortedTableData();
            }

            return new MyleTableColumnOptions({
                HeaderText: headerText,
                FieldName: fieldName,
                IsHidden: isHidden,
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: sortColumn,
                SortDirection: queryOrderDirection,
                IsSortActive: queryOrderType === sortColumn,
                WidthPercentageDefault: WidthPercentage,
                sortingMethod: () => tableSort(sortColumn)
            });
        };

        const prescriptionRenewalsDefaultColumns = [
            getCommonTableColumnOptions(getText("MedicationLabel"), medicationColumnKey, PrescriptionRenewalHistoryOrderType.Medication, 20),
            getCommonTableColumnOptions(getText("OriginLabel"), "origin", PrescriptionRenewalHistoryOrderType.Origin, 10),
            getCommonTableColumnOptions(cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields ? getText("TreatmentStartLabel") : getText("BeginningLabel"), beginningColumnKey, PrescriptionRenewalHistoryOrderType.Beginning, 10),
            getCommonTableColumnOptions(getText("EndFieldLabel"), endColumnKey, PrescriptionRenewalHistoryOrderType.End, 10),
            getCommonTableColumnOptions(getText("DosageLabel"), dosageColumnKey, PrescriptionRenewalHistoryOrderType.Dosage, 10),
            getCommonTableColumnOptions(getText("QuantityLabel"), quantityColumnKey, PrescriptionRenewalHistoryOrderType.Quantity, 10),
            getCommonTableColumnOptions(getText("NumberOfRefillLabel"), "refills", PrescriptionRenewalHistoryOrderType.Refills, 5),
            getCommonTableColumnOptions(getText("DurationLabel"), "duration", PrescriptionRenewalHistoryOrderType.Duration, 5),
            getCommonTableColumnOptions(getText("RequestedByLabel"), prescriberColumnKey, PrescriptionRenewalHistoryOrderType.Prescriber, 10, cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields),
            getCommonTableColumnOptions(getText("StatusLabel"), "status", PrescriptionRenewalHistoryOrderType.Status, 10)
        ];

        if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
            prescriptionRenewalsDefaultColumns.push(
                getCommonTableColumnOptions(getText("MedicalSummary_Medication_WrittenDateLabel"), writtenDateColumnKey, PrescriptionRenewalHistoryOrderType.WrittenDate, 10, true)
            );
        }

        return prescriptionRenewalsDefaultColumns;
    };

    function getEmployeeByContactId(employeeId: Guid): Employee {
        if (!cachedEmployees?.current) {
            return null;
        }

        if (!Object.prototype.hasOwnProperty.call(cachedEmployees.current, employeeId)) {
            cachedEmployees.current[employeeId] = cacheStore.GetEmployeeByContactFacetId(employeeId);
        }

        return cachedEmployees.current[employeeId];
    }

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<PrescriptionRenewalsMedicationHistoryItem>>(
            columnsOptions,
            column => ({
                renderCell: item => renderTextCell(column.FieldName, item)
            }),
            false
        );
    }

    const getRowCellsCssClass = (data: PrescriptionRenewalsMedicationHistoryItem): string => {
        switch (data.statusId) {
            case 1:
                return redRowString;

            case 2:
                return greenRowString;

            default:
                return "";
        }
    };

    const formatCellText = (fieldName: string, item: TableDataSourceItem<PrescriptionRenewalsMedicationHistoryItem>): string | JSX.Element => {
        function renderMultipleString(multipleString: string[]): JSX.Element {
            return (
                <>
                    {multipleString.map((p: string, idx: number) => (
                        <div key={p + idx}>
                            {p}
                        </div>
                    ))}
                </>
            );
        }

        switch (fieldName) {
            case beginningColumnKey:
                if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
                    return PatientMedicalItemsHelper.GetLifeStageDateString(
                        LuxonDateTimeParser.parse(item.Model.beginning)?.toLocal()?.toJSDate(),
                        item.Model.startDatePrecision
                    ) ?? "";
                }
                else {
                    return LuxonDateTimeParser.parse(item.Model.beginning)?.toLocal().toFormat(fixedFormat) ?? "";
                }
            case endColumnKey:
                return LuxonDateTimeParser.parse(item.Model.end)?.toLocal().toFormat(fixedFormat) ?? "";
            case prescriberColumnKey:
                return cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && PrescriptionHelper.IsPrescribedExternally(item.Model.externalPrescriberDisplayName)
                    ? item.Model.externalPrescriberDisplayName
                    : Employee.getFullNameWithPrefixContactFacet(item.Model.prescriber?.ContactFacet_id) ?? "";
            case quantityColumnKey:
                return renderMultipleString(item.Model.quantity);
            case dosageColumnKey:
                return renderMultipleString(item.Model.dosage);
            case writtenDateColumnKey:
                return PatientMedicalItemsHelper.GetLifeStageDateString(
                    item.Model.writtenDate,
                    item.Model.writtenDatePrecision
                ) ?? "";
            default:
                return item.Model[fieldName];
        }
    };

    const renderTextCell = (fieldName: string, item: TableDataSourceItem<PrescriptionRenewalsMedicationHistoryItem>): JSX.Element => {
        const text = formatCellText(fieldName, item);

        if (fieldName === medicationColumnKey) {
            const { instruction, ceasedReason } = item.Model;

            return (
                <>
                    <div className={styles.medicationNameContainer}>
                        {cacheStore.ClinicSettings.EnableLongTermMedication && item.Model.isLongTerm && (
                            <LongTermMedicationIcon dataCy="prescriptionRenewals-medicationHistory-longTermIcon" />
                        )}
                        <div className={styles.fontWeightBold + " " + getRowCellsCssClass(item.Model)}>{text}</div>
                    </div>
                    {item.Model.isCeased && (
                        <div className={styles.ceasedReason}>
                            <SpanControl text={ceasedReason} />
                        </div>
                    )}
                    {filterSettings.isSelectedDetailsCheck && instruction &&
                        <div className={styles.medicationInstruction}>{instruction}</div>
                    }
                </>
            );
        }
        else if (fieldName === endColumnKey) {
            const color: string = getRowCellsCssClass(item.Model);
            const isCeaseDateValid: boolean = !!item.Model.ceaseDate;
            let isRed = isCeaseDateValid ? true : color === redRowString;
            let isGreen = isCeaseDateValid ? false : color === greenRowString;

            return <div><SpanControl IsRed={isRed} IsGreen={isGreen} text={text} /></div>;
        }
        else if (
            fieldName == prescriberColumnKey
            && cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
            && !IsNullOrWhiteSpace(item.Model.externalPrescriberDisplayName)
        ) {
            const id = `prescription-renewals-view-medication-history-external-clinician-${uuid.v4()}`;
            return (
                <>
                    <div id={id} className={getRowCellsCssClass(item.Model)}>{text}</div>
                    <Tooltip
                        anchorSelect={`#${id}`}
                        place="bottom-end"
                    >
                        {cacheStore.GetString("MedicalSummary_Medication_ExternalClinicianLabel")}
                    </Tooltip>
                </>
            );
        }

        return <div className={getRowCellsCssClass(item.Model)}>{text}</div>;
    };

    const getProvincialMedicationButton = (): JSX.Element[] => {
        const elements: JSX.Element[] = [];
        const hasPharmaNetAccess = cacheStore.hasPharmaNetAccess();

        if (hasPharmaNetAccess) {
            elements.push(
                <div
                    style={{ display: "inherit", alignItems: "center", flexWrap: "nowrap", gap: "10px" }}
                    key="dsqAndPharmaNetAccessButtons"
                >
                    <button
                        className="btn btn-primary"
                        onClick={() => setIsPharmaNetLoginOpen(true)}
                    >
                        {getText("PharmaNet")}
                    </button>
                </div>
            );
        }
        return elements;
    };

    const getPharmacoTherapeuticButton = (): JSX.Element => {
        if (!settingsStore.configuration.get("PharmacoTherapeuticActive"))
            return null;

        return (
            <VigilanceIcon
                key="vigilanceKey"
                id="_vigilanceLink"
                height="27px"
                onClick={() => VigilanceQueryViewerPopupHelper.openVigilancePopupForSearch()}
            />
        );
    };

    const toggleFilterSettingPopup = (): void => {
        setIsFilterSettingsOpen(p => !p);
    };

    const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, getMyleTableColumnsDefaultOptions());
    const tableRenderOptions = loadTableRenderOptions(columnsOptions);
    const columnOptionsForOrderColumnsPopup = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    const printMediactions = async (): Promise<void> => {
        const activeText = "ActiveLabel";
        const expiredText = "Expired3Label";
        const ceasedText = "CeasedLabel";
        const completedText = "Completed2Label";

        const medicationStatus = (filterSettings.selectedStatus.length > 0) ? filterSettings.selectedStatus : [activeText, expiredText, ceasedText, completedText];
        const medicationOrigins = (filterSettings.selectedOrigin.length > 0) ? filterSettings.selectedOrigin : ["Rx", "MYLE"];
        const requestData = {
            PatientContactId: props.patientSummary.get("PatientContactId"),
            FromDate: filterSettings.dateFrom,
            ToDate: filterSettings.dateTo,
            Statuses: medicationStatus.map(status => {
                switch (status) {
                    case activeText: return MedicationsStatus.Active;
                    case expiredText: return MedicationsStatus.Expired;
                    case ceasedText: return MedicationsStatus.Ceased;
                    case completedText: return MedicationsStatus.Completed;
                    default: return MedicationsStatus.Unknown;
                }
            }),
            Origins: medicationOrigins.map(origin => {
                switch (origin) {
                    case "MYLE": return MedicationsOrigin.MYLE;
                    case "Rx": return MedicationsOrigin.Rx;
                    default: return MedicationsOrigin.Unknown;
                }
            }),
            SortColumn: queryOrderType,
            SortDirection: queryOrderDirection,
            SearchText: searchText,
            SearchRenewalFields: true
        };
        const response = await fetchMedicationHistoryPDF(requestData);
        if (!response) return;
        PrintHelper.ShowPrintPreviewFileName(response.FileName);
    };

    useEffect(() => {
        props.refreshPatientData();
    }, []);

    useEffect(() => {
        setFilteredAndSortedTableData();
    }, [filterSettings, searchText]);

    useEffect(() => {
        cachedMedicationHistoryItems.current = props.patientSummary?.get("FilteredMedicalItemsMedicationAndPrescription")?.toArray().map(
            p => PrescriptionRenewalsMedicationHistoryItem.convertMedicationModel(p, getEmployeeByContactId(p.Model.EmployeeId))
        ) ?? [];

        setFilteredAndSortedTableData();
    }, [props.patientSummary]);

    useEffect(() => {
        latestFilterSettings.current = filterSettings;
    }, [filterSettings]);

    useEffect(() => {
        const { prescriptionRenewalId } = props;
        const savedHistoryFilterSetting = prescriptionRenewalsStore.prescriptionRenewalHistoryFilterSetting[prescriptionRenewalId];

        if (!!savedHistoryFilterSetting) {
            setFilterSettings(savedHistoryFilterSetting);
        }

        return () => {
            sessionStore.dispatch(
                LocalOperations.PrescriptionRenewal.SetPrescriptionRenewalHistoryFilterSetting,
                {
                    prescriptionRenewalId,
                    prescriptionRenewalHistoryFilterSetting: latestFilterSettings.current
                }
            );
        };
    }, [props.prescriptionRenewalId]);

    return (
        <div className={styles.prescriptionRenewalViewContainer}>
            {isPharmaNetLoginOpen && (
                <PharmaNetCredentials
                    onClose={() => setIsPharmaNetLoginOpen(false)}
                    patient={props.patientProfile}
                    context={PharmaNetContext.Patient}
                />
            )}
            <CommonTableHeaderComponent
                title={getText("MedicationsLabel")}
                additionalLeftComponent={getProvincialMedicationButton()}
                additionalRightComponent={[getPharmacoTherapeuticButton()]}
                searchText={searchText}
                searchPlaceholder={getText("SearchField")}
                onSearch={text => setSearchText(text)}
                onRefreshClick={() => { props.refreshPatientData(); }}
                onSettingsClick={() => toggleFilterSettingPopup()}
                onPrintClick={() => printMediactions()}
            />
            {isFilterSettingsOpen && (
                <PrescriptionRenewalsMedicationHistoryFilterSettings
                    settings={filterSettings}
                    onClose={() => toggleFilterSettingPopup()}
                    onSaveChanges={settings => {
                        setFilterSettings(settings);
                        setIsFilterSettingsOpen(false);
                    }}
                    onColumnOrderingPopupOpenClick={() => {
                        setIsColumnOrderingPopupVisible(p => !p);
                        toggleFilterSettingPopup();
                    }}
                />
            )}
            {isColumnOrderingPopupVisible && (
                <MyleTableColumnOrderControlv2
                    TableColumns={columnOptionsForOrderColumnsPopup}
                    SaveOptionskeyTarget={saveOptionKey}
                    ContainerId="PrescriptionRenewalMedicationHistoryColumnSettingsPopup"
                    closeColumnOrderPopup={() => setIsColumnOrderingPopupVisible(p => !p)}
                />
            )}
            <MyleTable
                TableRenderOptions={tableRenderOptions}
                TableId="prescriptionRenewalsHistorytable"
                Columns={getTableColumns(columnsOptions)}
                DataSource={medicationHistoryItem}
                ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                RowClickOperation={null}
                SaveOptionskeyTarget={saveOptionKey}
                CssClass={styles.prescriptionRenewalViewTable}
                TableDataCy="prescriptionRenewals-medicationHistoryTable"
            />
        </div>
    );
}

export { PrescriptionRenewalsViewMedicationHistory };
